import { useState, useEffect, useMemo, useCallback } from 'react';
import { viewOffer, Offer } from '../api/viewOfferApiService';
import {
  getStatusLabel as getSharedStatusLabel,
  getStatusBadgeClasses as getSharedStatusBadgeClasses,
  StatusValue,
} from '@/shared/utils/statusBadge';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Gets the status label in Polish
 * Re-exported from shared utils for convenience
 */
export const getStatusLabel = (
  status: StatusValue | null | undefined
): string => {
  return getSharedStatusLabel(status);
};

/**
 * Gets status badge classes based on status
 * Re-exported from shared utils for convenience
 */
export const getStatusBadgeClasses = (
  status: StatusValue | null | undefined
): { container: string; dot: string } => {
  return getSharedStatusBadgeClasses(status);
};

// ============================================================================
// Hook Return Type
// ============================================================================

export interface UseViewOfferReturn {
  // Data state
  offerData: Offer | undefined;
  offer: Offer | undefined;
  isLoading: boolean;
  showErrorScreen: boolean;

  // Computed values
  statusLabel: string;
  statusBadgeClasses: { container: string; dot: string };

  // Actions
  refetch: () => Promise<void>;
}

// ============================================================================
// useViewOffer Hook
// ============================================================================

/**
 * Custom hook for managing ViewOffer page state and logic
 * Handles fetching offer data, error states, and computed values
 */
export const useViewOffer = (offerId: string): UseViewOfferReturn => {
  // State
  const [offerData, setOfferData] = useState<Offer | undefined>(undefined);
  const [showErrorScreen, setShowErrorScreen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch offer data function
  const fetchOfferData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await viewOffer({ offerId });
      if (response.success && response.data) {
        setOfferData(response.data);
        setShowErrorScreen(false);
      } else {
        setShowErrorScreen(true);
        console.error('Error fetching offer data:', response.error);
      }
    } catch (error) {
      setShowErrorScreen(true);
      console.error('Error fetching offer data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [offerId]);

  // Fetch offer data on mount and when offerId changes
  useEffect(() => {
    fetchOfferData();
  }, [fetchOfferData]);

  // Computed values
  const offer = useMemo(() => offerData, [offerData]);

  const statusLabel = useMemo(
    () => getStatusLabel(offer?.status as StatusValue),
    [offer?.status]
  );

  const statusBadgeClasses = useMemo(
    () => getStatusBadgeClasses(offer?.status as StatusValue),
    [offer?.status]
  );

  return {
    // Data state
    offerData,
    offer,
    isLoading,
    showErrorScreen,

    // Computed values
    statusLabel,
    statusBadgeClasses,

    // Actions
    refetch: fetchOfferData,
  };
};
